<template>
  <BasicModal
    :width="644"
    @register="register"
    v-bind="$attrs"
    :title="t('pi.index.readPiDisclaimer')"
    :canFullscreen="false"
    @cancel="cancelClick"
    @visible-change="init"
  >
    <article :class="prefixCls">
      <!-- 申购条款 -->
      <section
        ref="agreeBox"
        class="tradeTip min-h-50 px-3 pt-3 mb-2"
        :style="{
          'max-height': maxHeight,
        }"
      >
        <Alert
          ref="alterRef"
          class="!sticky top-1/2 transform !inline-flex w-auto !backdrop-filter !backdrop-blur-md"
          :class="{ 'opacity-0': !showAlter, '-translate-y-1/2': tradeNotice }"
          :style="{ left: `${getLeft}px` }"
          :message="alterMessage"
          type="warning"
          show-icon
        >
          <template #icon>
            <Icon icon="M-terminal-iconattention|ali" size="24" color="#FF9852" />
          </template>
        </Alert>
        <div class="pl-6" :class="{ '-mt-10': tradeNotice }" v-html="tradeNotice"> </div>
      </section>
    </article>
    <!-- 底部按钮 -->
    <template #footer>
      <footer :class="`${prefixCls}-footer`" class="flex justify-between items-center">
        <div class="flex flex-col justify-start">
          <Checkbox
            class=""
            v-if="readMe"
            v-model:checked="checked"
            @change="handleTradeCheckChange"
          >
            {{ t('pi.index.agreeDisclaimer') }}
          </Checkbox>
        </div>
        <div>
          <a-button v-if="!checked" class="!px-4 !rounded-lg !text-xs" :disabled="true">
            {{ t('pi.index.readDisclaimer') }}
          </a-button>
          <a-button
            v-else
            class="!px-4 !rounded-lg !text-xs"
            type="primary"
            :loading="loading"
            @click="onOk"
          >
            {{ t('pi.index.iAgree') }}
          </a-button>
        </div>
      </footer>
    </template>
  </BasicModal>
</template>
<script setup lang="ts">
  import { BasicModal, useModalInner } from '/@/components/Modal';
  import { useI18n, useLocale } from '@mcex/locale';
  import { useDesign } from '/@/hooks/web/useDesign';
  import { Checkbox, Alert } from 'ant-design-vue';
  import DOMPurify from 'dompurify';
  import {
    useAgreeTip,
    useConfirmModal,
  } from '/@/views/exchangeService/buying_etp/Modals/useConfirmModal';
  import { computed, ref } from 'vue';
  import Icon from '/@/components/Icon';
  import { useElementSize } from '@vueuse/core';
  import { queryProfessionalInvestorApi, confirmProfessionalInvestorApi } from '/@/api/question';

  const props = defineProps<{
    identificationCode: string;
  }>();
  const [register, { closeModal }] = useModalInner();
  const { prefixCls } = useDesign('confirm-modal-box');

  const { loading, setLoading } = useConfirmModal();

  const { t } = useI18n();
  const emit = defineEmits(['ok']);

  const cancelClick = () => {
    closeModal();
  };
  const alterMessage = ref('');
  const tradeNotice = ref('');
  const statementId = ref(0);

  const {
    readMe,
    checked,
    agreeBox,
    checkChange,
    showAlter,
    checkAgree,
    init: initAgree,
    isBottom: tradeIsBottom,
  } = useAgreeTip(tradeNotice);
  const handleTradeCheckChange = (e) => {
    // 将要勾选且未滚动到底部时
    if (checked.value && !tradeIsBottom.value) {
      alterMessage.value = t('pi.index.agreeNotice');
    }
    checkChange(e);
  };

  const onOk = async () => {
    if (!checkAgree()) {
      alterMessage.value = t('pi.index.agreeNotice');
      return;
    }
    setLoading(true);
    const isSuccess = await confirmProfessionalInvestorApi({
      statementId: statementId.value,
      identificationCode: props.identificationCode,
    });
    setLoading(false);
    if (isSuccess) {
      emit('ok');
    }
  };
  const queryProfessionalInvestor = async () => {
    const res = await queryProfessionalInvestorApi();
    tradeNotice.value = DOMPurify.sanitize(res.content || '');
    statementId.value = res.id || 0;
  };

  const init = (visible) => {
    if (visible) {
      statementId.value = 0;
      queryProfessionalInvestor();
    }
    /**
     * 关闭时重置uuid
     */
    initAgree();
  };

  const { getLocale } = useLocale();

  const maxHeight = computed(() => {
    if (getLocale.value === 'en') {
      return '16rem';
    }
    return '20rem';
  });

  const alterRef = ref();
  const { width: agreeWidth } = useElementSize(agreeBox);
  const { width: alterWidth } = useElementSize(alterRef);
  const getLeft = computed(() => {
    return (agreeWidth.value - alterWidth.value) / 2;
  });
</script>

<style lang="less" scoped>
  @prefix-cls: ~'@{namespace}-confirm-modal-box';

  .@{prefix-cls} {
    .account-label {
      color: @primary-color;
    }

    .sub-label {
      padding-right: 8px;
      color: @text-color-64;
    }

    .tradeTip {
      .text-sm(400);
      background-color: @component-bg-color3;
      color: @text-color-64;
      border-radius: 8px;
      overflow: auto;

      .title {
        background-color: @component-bg-color3;
        color: @text-color-64;
      }
    }

    .tip-type-box {
      & :deep(> div) {
        width: 100%;

        & > label {
          width: 50%;
          text-align: center;
        }
      }
    }
  }
</style>
